import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'tap.theme';

/**
 * One store, shared by every consumer.
 *
 * Module scope rather than useState, because the sidebar is mounted twice on a phone —
 * the desktop one is rendered and hidden by a media query, the drawer one is real. Two
 * useState copies meant two answers to "is it dark", and whichever effect ran last
 * won. Here there is one value and one write path.
 */
let current: Theme = readStored();
const listeners = new Set<() => void>();

function readStored(): Theme {
  try {
    return localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    // Private-mode Safari throws on localStorage. Light is the default anyway.
    return 'light';
  }
}

/**
 * Writes the attribute the CSS reads. ALWAYS one of the two — never absent — so
 * :root is unambiguously the light theme and no OS-level rule participates.
 */
export function applyTheme(theme: Theme): void {
  current = theme;
  document.documentElement.setAttribute('data-theme', theme);

  // Keeps the phone's status bar and the desktop title bar in step with the page.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#0a0a0a' : '#f7f7f8');

  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // A remembered preference is a nicety; the current session still works without it.
  }
  listeners.forEach((notify) => notify());
}

export function useTheme() {
  const theme = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => current,
    () => 'light' as Theme,
  );

  const toggle = useCallback(() => {
    applyTheme(current === 'dark' ? 'light' : 'dark');
  }, []);

  return { theme, isDark: theme === 'dark', toggle };
}
